import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("ds_token");
    if (!token) { setLoading(false); return; }
    let cancelled = false;
    // Restore the session from a stored JWT. Only a genuine 401 (invalid/expired
    // token) clears it — transient network/5xx errors are retried and never log the
    // user out on refresh.
    const bootstrap = async () => {
      for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
        try {
          const r = await api.get("/auth/me");
          if (!cancelled) { setUser(r.data); setLoading(false); }
          return;
        } catch (err) {
          if (err.response?.status === 401) {
            localStorage.removeItem("ds_token");
            if (!cancelled) setLoading(false);
            return;
          }
          await new Promise((res) => setTimeout(res, 800)); // transient — keep token, retry
        }
      }
      if (!cancelled) setLoading(false); // retries exhausted; token preserved
    };
    bootstrap();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email: (email || "").trim(), password });
    localStorage.setItem("ds_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("ds_token");
    setUser(null);
    // Hard replace wipes in-memory state and removes the authenticated page from
    // history so the browser Back button cannot reopen the dashboard.
    window.location.replace("/login");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
